import { create } from 'zustand';
import {
  applyPrimaryShadesToElement,
  applyAccentColorToElement,
  clearAccentColorsFromElement,
  SURFACE_TOKENS,
  surfaceTokens,
  whiteLink,
  toHex,
} from '../lib/color';
import type { CompiledAppStyle } from '../types/domainConfig';

/**
 * Branding state — ports `BrandingService` from the Angular member portal.
 *
 * Two layers, applied bottom-up:
 *   - `baseBranding`  — vertical/hub default (resolved during cold start
 *                       before tenant context is known). Used as fallback
 *                       when a child tenant omits logos / colors.
 *   - active branding — pushed via setBranding() once a tenant is resolved.
 *                       Overlays base branding; missing fields fall through.
 *
 * On top of both sits the tenant's app style (colours, fonts, corners) from
 * the domain config, set via setAppStyle(). Its colours win over branding's.
 *
 * On every change we mutate `:root` CSS variables, the favicon, and the
 * `theme-color` meta. Branding and the app style are also persisted to
 * localStorage so the next cold start can paint them before the API call returns.
 */

export interface BrandingConfig {
  domain: string;
  name: string;
  /** Used as `branding_key` API header for tenant resolution downstream. */
  brandingKey: string;
  /** Display product name (longer than `name` in some tenants). */
  productName: string;
  logoUrl: string;
  logoSmallUrl: string;
  primaryColor?: string;
  /** Brand accent — drives the brass/mustard accent token. */
  secondaryColor?: string;
  /** Secondary brand accent — drives the tertiary accent token. */
  tertiaryColor?: string;
  faviconUrl?: string;
  logoLargeUrl?: string;
}

/** Shape returned by Core/Tenant API `/resolve-domain` and `/branding`. */
export interface ApiBrandingData {
  name?: string;
  product_name?: string;
  branding_key?: string;
  logo_large_url?: string;
  logo_small_url?: string;
  favicon_url?: string;
  primary_color?: string;
  secondary_color?: string;
  tertiary_color?: string;
}

const STORAGE_KEY = 'paas_branding';
/**
 * The hub/base branding is persisted separately so it survives reloads.
 * Without this `baseBranding` is in-memory only and is lost on refresh —
 * a child community then has nothing to overlay and its missing logos/
 * colors fall back to hard defaults instead of the SSO hub's branding.
 */
const BASE_STORAGE_KEY = 'paas_base_branding';
const APP_STYLE_STORAGE_KEY = 'paas_app_style';
const APP_STYLE_FONTS_LINK_ID = 'app-style-fonts';
/** Multiplies every corner radius token. Mirrors CORNER_SCALE in the portal's App Style tab. */
const CORNER_SCALE: Record<CompiledAppStyle['corners'], number> = { sharp: 0.4, rounded: 1, soft: 1.4 };
const DEFAULT_LOGO = '/assets/logos/default/logo.svg';
const DEFAULT_LOGO_SMALL = '/assets/logos/default/logo-small.svg';
const DEFAULT_FAVICON = '/assets/logos/default/favicon.ico';
const FALLBACK_FAVICON = '/favicon.ico';
/** Fancourt forest green — matches the `--primary-*` token default. */
const DEFAULT_PRIMARY_COLOR = '#3a5a40';
const DEFAULT_BRANDING_KEY = 'PaaS';
const DEFAULT_NAME = 'IP4Estates';
const DEFAULT_PRODUCT_NAME = 'IP4Estates';

interface BrandingState {
  branding: BrandingConfig | null;
  baseBranding: BrandingConfig | null;
  appStyle: CompiledAppStyle | null;

  /** Hub / vertical default. Populated during domain resolution before tenant overlay. */
  setBaseBrandingFromResolution: (
    domain: string,
    name?: string,
    primaryColor?: string,
    apiBranding?: ApiBrandingData,
  ) => void;

  /** Set tenant-active branding, falling back to baseBranding for missing fields. */
  setBranding: (
    domain: string,
    name?: string,
    primaryColor?: string,
    apiBranding?: ApiBrandingData,
  ) => void;

  /** Apply the active tenant's app style from its domain config; null drops it back to branding alone. */
  setAppStyle: (appStyle: CompiledAppStyle | null) => void;

  /** Apply default placeholder branding while waiting for API. */
  setDefaultBranding: (domain: string) => void;

  /**
   * Full branding wipe (login storage reset, tests). Routine sign-out should
   * NOT call this — member portal keeps domain branding on the same hostname.
   */
  clearBranding: () => void;

  /**
   * Re-apply the hub/base branding as the active branding. Used when a
   * resident backs out of a community selection so the screen stops
   * showing the half-selected community's colors.
   */
  revertToBaseBranding: () => void;

  getBrandingKey: () => string;
  getProductName: () => string;
  getLogoUrl: () => string;
  getLogoSmallUrl: () => string;
  getOrganizationName: () => string;

  /** Hydrate from localStorage on first import. */
  _hydrate: () => void;
}

function buildBranding(
  domain: string,
  name: string | undefined,
  primaryColor: string | undefined,
  apiBranding: ApiBrandingData | undefined,
  base: BrandingConfig | null,
): BrandingConfig {
  const finalName = apiBranding?.name || name || base?.name || DEFAULT_NAME;
  const finalProductName =
    apiBranding?.product_name || finalName || base?.productName || DEFAULT_PRODUCT_NAME;
  return {
    domain,
    name: finalName,
    brandingKey: apiBranding?.branding_key || base?.brandingKey || DEFAULT_BRANDING_KEY,
    productName: finalProductName,
    logoUrl: apiBranding?.logo_large_url || base?.logoUrl || DEFAULT_LOGO,
    logoSmallUrl:
      apiBranding?.logo_small_url ||
      apiBranding?.logo_large_url ||
      base?.logoSmallUrl ||
      DEFAULT_LOGO_SMALL,
    primaryColor: apiBranding?.primary_color || primaryColor || base?.primaryColor || DEFAULT_PRIMARY_COLOR,
    secondaryColor: apiBranding?.secondary_color || base?.secondaryColor,
    tertiaryColor: apiBranding?.tertiary_color || base?.tertiaryColor,
    faviconUrl: apiBranding?.favicon_url || base?.faviconUrl || DEFAULT_FAVICON,
    logoLargeUrl: apiBranding?.logo_large_url || base?.logoLargeUrl,
  };
}

function applySideEffects(branding: BrandingConfig, appStyle: CompiledAppStyle | null): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;

  // 1. The app style's colours win over branding's.
  const primaryColor = appStyle?.primary_color ?? branding.primaryColor;
  const secondaryColor = appStyle?.secondary_color ?? branding.secondaryColor;
  const tertiaryColor = appStyle?.tertiary_color ?? branding.tertiaryColor;
  if (primaryColor) {
    applyPrimaryShadesToElement(root, primaryColor);
  }

  // 2. Brand accents — secondary drives the brass/mustard, tertiary the
  // tertiary accent. Cleared first so absent values fall back to the
  // tokens.css defaults instead of keeping a dropped style's accents.
  clearAccentColorsFromElement(root);
  if (secondaryColor) {
    applyAccentColorToElement(root, '--accent-brass', '--accent-brass-ink', secondaryColor);
  }
  if (tertiaryColor) {
    applyAccentColorToElement(root, '--accent-tertiary', '--accent-tertiary-ink', tertiaryColor);
  }

  // 3. Surfaces, fonts and corners, then the browser chrome.
  applyAppStyleTokens(root, appStyle, primaryColor || DEFAULT_PRIMARY_COLOR);
  applyFavicon(branding.faviconUrl);
  applyThemeColor(primaryColor || DEFAULT_PRIMARY_COLOR);
}

/**
 * Point the surface, font and radius tokens (see tailwind.config.ts) at the app style, and load its
 * fonts from Google Fonts. Without a style the tokens fall back to their built-in values.
 */
function applyAppStyleTokens(root: HTMLElement, appStyle: CompiledAppStyle | null, primaryColor: string): void {
  // 1. Start from the built-in surfaces; white surfaces still need a deeper shade for a very light primary.
  for (const key of SURFACE_TOKENS) root.style.removeProperty(key);
  const whiteShade = whiteLink(primaryColor);
  if (whiteShade) root.style.setProperty('--white-link', whiteShade);

  if (!appStyle) {
    for (const key of ['--font-heading', '--font-body', '--radius-scale']) root.style.removeProperty(key);
    document.getElementById(APP_STYLE_FONTS_LINK_ID)?.remove();
    return;
  }

  // 2. Background and card colours, each with text that reads on it. Styles saved before
  //    either existed keep the built-in surface.
  const surfaces = {
    ...(appStyle.background_color && surfaceTokens('paper', appStyle.background_color, primaryColor)),
    ...(appStyle.card_color && surfaceTokens('card', appStyle.card_color, primaryColor)),
  };
  for (const [key, value] of Object.entries(surfaces)) root.style.setProperty(key, value);

  // 3. Fonts and corners.
  root.style.setProperty('--font-heading', `"${appStyle.heading_font}"`);
  root.style.setProperty('--font-body', `"${appStyle.body_font}"`);
  root.style.setProperty('--radius-scale', String(CORNER_SCALE[appStyle.corners]));

  // Every offered family has 400 and 700; the browser picks the nearest for other weights.
  const families = [...new Set([appStyle.heading_font, appStyle.body_font])]
    .map((font) => `family=${font.replace(/ /g, '+')}:wght@400;700`)
    .join('&');
  const href = `https://fonts.googleapis.com/css2?${families}&display=swap`;
  let link = document.getElementById(APP_STYLE_FONTS_LINK_ID) as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement('link');
    link.id = APP_STYLE_FONTS_LINK_ID;
    link.rel = 'stylesheet';
    document.head.appendChild(link);
  }
  if (link.href !== href) link.href = href;
}

function applyFavicon(faviconUrl: string | undefined): void {
  if (!faviconUrl) return;
  const img = new Image();
  img.onload = () => setFavicons(faviconUrl);
  img.onerror = () => setFavicons(faviconUrl === DEFAULT_FAVICON ? FALLBACK_FAVICON : DEFAULT_FAVICON);
  img.src = faviconUrl;
}

function setFavicons(href: string): void {
  const head = document.head;
  let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    head.appendChild(link);
  }
  link.href = href;

  let appleTouch = document.querySelector("link[rel='apple-touch-icon']") as HTMLLinkElement | null;
  if (!appleTouch) {
    appleTouch = document.createElement('link');
    appleTouch.rel = 'apple-touch-icon';
    head.appendChild(appleTouch);
  }
  appleTouch.href = href;
}

function applyThemeColor(color: string): void {
  const hex = toHex(color) ?? DEFAULT_PRIMARY_COLOR;
  const themeMeta = document.querySelector("meta[name='theme-color']") as HTMLMetaElement | null;
  if (themeMeta) themeMeta.content = hex;
  const appleStatusMeta = document.querySelector(
    "meta[name='apple-mobile-web-app-status-bar-style']",
  ) as HTMLMetaElement | null;
  if (appleStatusMeta) appleStatusMeta.content = 'black-translucent';
}

function persist(branding: BrandingConfig, key: string = STORAGE_KEY): void {
  try {
    localStorage.setItem(key, JSON.stringify(branding));
  } catch (error) {
    console.warn('[branding] Failed to persist:', error);
  }
}

export const useBrandingStore = create<BrandingState>((set, get) => ({
  branding: null,
  baseBranding: null,
  appStyle: null,

  setBaseBrandingFromResolution(domain, name, primaryColor, apiBranding) {
    const next = buildBranding(domain, name, primaryColor, apiBranding, null);
    set({ baseBranding: next });
    // Persist so the hub base survives reloads and stays the underlay for
    // any community the resident later switches into.
    persist(next, BASE_STORAGE_KEY);
  },

  setBranding(domain, name, primaryColor, apiBranding) {
    const { baseBranding } = get();
    const next = buildBranding(domain, name, primaryColor, apiBranding, baseBranding);
    set({ branding: next });
    persist(next);
    applySideEffects(next, get().appStyle);
  },

  setAppStyle(appStyle) {
    set({ appStyle });
    try {
      if (appStyle) localStorage.setItem(APP_STYLE_STORAGE_KEY, JSON.stringify(appStyle));
      else localStorage.removeItem(APP_STYLE_STORAGE_KEY);
    } catch (error) {
      console.warn('[branding] Failed to persist app style:', error);
    }
    // Before the first branding lands there is nothing to restyle yet; setBranding applies it then.
    const { branding } = get();
    if (branding) applySideEffects(branding, appStyle);
  },

  setDefaultBranding(domain) {
    get().setBranding(domain, DEFAULT_NAME, DEFAULT_PRIMARY_COLOR);
  },

  revertToBaseBranding() {
    const { baseBranding } = get();
    if (!baseBranding) return;
    set({ branding: baseBranding });
    persist(baseBranding);
    applySideEffects(baseBranding, get().appStyle);
  },

  clearBranding() {
    try {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(BASE_STORAGE_KEY);
      localStorage.removeItem(APP_STYLE_STORAGE_KEY);
    } catch { /* noop */ }
    set({ branding: null, baseBranding: null, appStyle: null });
    if (typeof document !== 'undefined') {
      // Reset palette to defaults (re-evaluated from tokens.css on next paint).
      const root = document.documentElement;
      for (const k of ['--primary-50','--primary-100','--primary-200','--primary-300','--primary-400','--primary-500','--primary-600','--primary-700','--primary-800','--primary-900','--primary-ink']) {
        root.style.removeProperty(k);
      }
      clearAccentColorsFromElement(root);
      applyAppStyleTokens(root, null, DEFAULT_PRIMARY_COLOR);
    }
  },

  getBrandingKey() {
    return get().branding?.brandingKey ?? DEFAULT_BRANDING_KEY;
  },
  getProductName() {
    return get().branding?.productName ?? DEFAULT_PRODUCT_NAME;
  },
  getLogoUrl() {
    return get().branding?.logoUrl ?? DEFAULT_LOGO;
  },
  getLogoSmallUrl() {
    return get().branding?.logoSmallUrl ?? DEFAULT_LOGO_SMALL;
  },
  getOrganizationName() {
    return get().branding?.name ?? DEFAULT_NAME;
  },

  _hydrate() {
    if (typeof window === 'undefined') return;
    try {
      // Restore the hub/base underlay first so the active branding (and any
      // later community switch) overlays it rather than falling to defaults.
      const rawBase = localStorage.getItem(BASE_STORAGE_KEY);
      if (rawBase) {
        set({ baseBranding: JSON.parse(rawBase) as BrandingConfig });
      }
      const rawAppStyle = localStorage.getItem(APP_STYLE_STORAGE_KEY);
      if (rawAppStyle) {
        set({ appStyle: JSON.parse(rawAppStyle) as CompiledAppStyle });
      }
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const branding = JSON.parse(raw) as BrandingConfig;
      set({ branding });
      applySideEffects(branding, get().appStyle);
    } catch (error) {
      console.warn('[branding] Failed to hydrate from storage:', error);
    }
  },
}));

/** Hydrate eagerly so cached branding paints before React renders. */
useBrandingStore.getState()._hydrate();
